import * as Sentry from '@sentry/nextjs';

import { API_VERSION } from '@/apis/constants';
import { getUserAuthLogout } from '@/apis/user/auth/logout';
import { GetUserAuthLogoutParams } from '@/apis/user/auth/logout.dto';
import { GetUserMaintenanceStatusResData } from '@/apis/user/maintenance/status.dto';
import AUTH_API_URL from '@/app/infra/api-url/auth';
import { ROUTE_CONFIG } from '@/routes/constants';
import { Route } from '@/routes/enum';
import { ResponseBody } from '@/typings/common';
import { ApiService, buildUrl } from '@/utils/api';
import { setToken, TokenKey } from '@/utils/token';

import { LoginResponse } from '../app/login/typings';

export const refreshAccessToken = async (token?: string, refreshToken?: string) => {
  try {
    if (!token || !refreshToken) {
      return;
    }
    const statusResponse: Response = await fetch(
      buildUrl({
        service: ApiService.USER,
        version: API_VERSION[ApiService.USER],
        path: `/maintenance/status`,
      }),
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      },
    );
    const statusData: ResponseBody<GetUserMaintenanceStatusResData> = JSON.parse(await statusResponse.text());

    if (statusData.data.isMaintenance) {
      throw new Error('System is under maintenance');
    }

    const response: Response = await fetch(AUTH_API_URL.REFRESH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ refreshToken }),
    });

    const data: ResponseBody<LoginResponse> = JSON.parse(await response.text());

    const {
      tokens: { token: newToken, refreshToken: newRefreshToken },
      user: { role, boundaryIds = [], departmentIds = [] },
    } = data.data || { tokens: {}, user: {} };

    return {
      token: newToken,
      refreshToken: newRefreshToken,
      role: role[0],
      boundaryId: boundaryIds[0] || '',
      departmentId: departmentIds?.[0] || '',
      boundaryIds,
      departmentIds,
    };
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

export async function clearStorageAndSignOut({
  callbackUrl,
  authLogoutQuery,
}: {
  callbackUrl?: string;
  authLogoutQuery: GetUserAuthLogoutParams['query'];
}) {
  Sentry.captureMessage('Clearing storage and signing out');
  try {
    await getUserAuthLogout({ query: authLogoutQuery });
  } catch (error) {
    // ignore error
  } finally {
    localStorage.clear();
    setToken(TokenKey.TOKEN, '');
    setToken(TokenKey.REFRESH_TOKEN, '');
    window.location.href = callbackUrl || ROUTE_CONFIG[Route.LOGIN].getFullUrl();
  }
}
